import api from './api';

// Dal Tile OTW upload (360 port of v1 imports/otw_dalt_updated.pl).
// Endpoints live under /daltile-otw-import/*. The axios `api` instance already
// carries the /api/v1 prefix.
const BASE = '/daltile-otw-import';

/** A whole-file rejection, AGGREGATED BY CAUSE — one entry per distinct problem,
 *  not per row. One bad mapping errors on every row that uses it, so a single
 *  unmapped broker can account for hundreds of rows and one line here. */
export interface FileError {
  message: string;
  /** Where the problem is fixed — almost always a maintenance page, because
   *  nearly every failure here is config rather than bad data in the file. */
  fix_hint: string;
  /** A sample of the source rows that hit this cause (capped). Labels, not
   *  numbers: a Dal Tile workbook can carry several sheets. */
  rows: string[];
  /** Total rows that hit it, including any beyond the sample. */
  count: number;
}

export interface FieldChange {
  column: string;
  old: string;
  new: string;
}

/** A difference the run REPORTS to the CSR by e-mail and does not act on. */
export interface Discrepancy {
  label: string;
  in_system: string;
  on_upload: string;
}

export type Verdict =
  | 'create' | 'update' | 'no_change' | 'old_move' | 'delivered' | 'deleted';

/** One collated container and what the run will do with it.
 *
 *  Several file rows per container is the NORMAL case here — Dal Tile's rows are
 *  per purchase order, so `lbs` and `piececount` are SUMS across the rows listed
 *  in `rows`, and each distinct PO becomes one master-receiving line. */
export interface ContainerPreview {
  container: string;
  verdict: Verdict;
  reason: string;
  rows: string[];

  rdc: string;
  scac: string;
  vessel: string;
  voyage: string;
  etadate: string;
  bol: string;
  po: string;
  commodity: string;
  size: string;
  equiptype: string;
  lbs: string;
  kgs: string;
  piececount: number;
  sealnumber: string;
  brokerrefno: string;
  shippername: string;
  gwimove: string;
  port_of_discharge: string;
  port_of_origin: string;
  broker: string;
  forwarder: string;
  carrier: string;

  template: string;
  rail: string;
  /** True when the container's SUMMED weight selected the overweight template. */
  overweight: boolean;

  pronumbers: string[];
  changes: FieldChange[];
  /** POs new on this move, each of which earns a master-receiving line. */
  new_pos: string[];
  /** The GWI warehouse the move delivers into, when it delivers into one. */
  warehouse: string;
  mr_lines: number;
  eta_warning: boolean;
  eta_past_warning: boolean;
  discrepancies: Discrepancy[];
  /** Set when legacy CMS's substring BOL/PO matching could have decided this
   *  container differently. Informational — the run does what `verdict` says. */
  legacy_divergence: string;
  /** Columns whose value disagreed between this container's rows. */
  conflicts: string[];
}

export interface PlanCounts {
  create: number;
  update: number;
  no_change: number;
  old_move: number;
  delivered: number;
  deleted: number;
}

export interface ValidateResponse {
  ok: boolean;
  filename: string;
  total_rows: number;
  counts: PlanCounts;
  containers: ContainerPreview[];
  errors: FileError[];
  eta_warnings: number;
  eta_past_warnings: number;
  legacy_divergences: number;
  /** Worksheets data was taken from — empty for a single-sheet file. */
  sheets: string[];
  mr_lines: number;
}

export interface ContainerResult {
  container: string;
  action: string;
  pronumber: string;
  ok: boolean;
  error: string;
  changed: string[];
  mr_lines: number;
  billto_flipped: boolean;
  went_overweight: boolean;
}

export interface ApplyResponse {
  ok: boolean;
  filename: string;
  batch_id: string;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  mr_lines: number;
  results: ContainerResult[];
  pronumbers: string[];
  errors: FileError[];
}

export interface DaltileOtwReferenceData {
  columns: string[];
  required_columns: string[];
  optional_columns: string[];
  overweight_lbs: number;
}

/** The `detail` string from a FastAPI HTTPException, or the best available text. */
export function extractDetail(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e instanceof Error) return e.message;
  return String(e);
}

export async function getReferenceData(): Promise<DaltileOtwReferenceData> {
  const res = await api.get(`${BASE}/reference-data`);
  return res.data as DaltileOtwReferenceData;
}

function upload(file: File): FormData {
  const form = new FormData();
  form.append('file', file);
  return form;
}

/** Dry run — writes nothing. */
export async function validateFile(file: File): Promise<ValidateResponse> {
  // The explicit multipart header is required; without it the file "goes
  // missing" server-side.
  const res = await api.post(`${BASE}/validate`, upload(file), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as ValidateResponse;
}

/** How many containers one request writes.
 *
 *  Chunking makes the request timeout irrelevant at ANY latency instead of
 *  correct at one we happened to measure, and gives the user progress on a job
 *  that legitimately takes a while. Deployed in-region a create is fast — the
 *  Home Depot port measured 17 in a few seconds — so this is about progress and
 *  safety, not about a performance problem.
 *
 *  50 rather than Home Depot's 25, because every chunk RE-PLANS the whole file
 *  and a Dal Tile file is much bigger: parsing and deciding the 2026-09-15
 *  production export (5,376 rows, 1,889 containers) measures 3.8s, and that cost
 *  is paid once per chunk. Its first run had 238 containers to write — 10 chunks
 *  and 38s of re-planning at 25, 5 chunks and 19s at 50. Halving the overhead
 *  while keeping a chunk's own work far inside the request timeout. */
export const CHUNK_SIZE = 50;

/** Run one chunk. `containers` names the subset; `final` tells the server to
 *  record the System Event for the whole run. */
export async function applyFile(
  file: File,
  opts: {
    containers?: string[]; batchId?: string; final?: boolean;
    /** What earlier chunks of this run already did. Only the final chunk
     *  records the System Event, so without these it would report its own
     *  slice as the whole run. */
    soFar?: { created: number; updated: number; mrLines: number };
  } = {},
): Promise<ApplyResponse> {
  const form = upload(file);
  if (opts.containers) form.append('containers', opts.containers.join(','));
  if (opts.batchId) form.append('batch_id', opts.batchId);
  form.append('final', String(opts.final ?? true));
  if (opts.soFar) {
    form.append('total_created', String(opts.soFar.created));
    form.append('total_updated', String(opts.soFar.updated));
    form.append('total_mr_lines', String(opts.soFar.mrLines));
  }
  const res = await api.post(`${BASE}/apply`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as ApplyResponse;
}

/** Display label for a verdict, matching the summary strip. */
export const VERDICT_LABELS: Record<Verdict, string> = {
  create: 'Create',
  update: 'Update',
  no_change: 'No change',
  old_move: 'Old move',
  delivered: 'Delivered',
  deleted: 'Deleted',
};

/** Which verdicts actually write. */
export const WRITES: Verdict[] = ['create', 'update'];
